import { inject, injectWithTransform, singleton } from "tsyringe";
import {
  countriesRequiringConsents,
  Logger,
  LoggerTransformer,
} from "@identity/utils";
import {
  ConsentCategoriesResolver,
  ConsentCallbackQueueResolver as IConsentCallbackQueueResolver,
} from "./types";
import { OnetrustApiConsentResolver } from "./onetrust/apiConsentResolver";
import { OnetrustCookieConsentResolver } from "./onetrust/cookieConsentResolver";
import { COOKIES, WINDOW } from "@identity/symbols";

const resolveTimeout = 5000; // TODO: set in ICBM per platform

const optInCategories: Categories = {
  necessary: true,
};

const optOutCategories: Categories = {
  necessary: true,
  performance: true,
  functional: true,
  targeting: true,
  social: true,
};

@singleton()
export class ConsentCallbackQueueResolver
  implements IConsentCallbackQueueResolver
{
  private readonly queue: ConsentCallbackQueue;
  private readonly optIn: boolean;

  private apiConsentResolver: ConsentCategoriesResolver;
  private cookieConsentResolver: ConsentCategoriesResolver;

  private categories: Categories;

  constructor(
    @inject(WINDOW) private container: Window,
    @injectWithTransform(
      Logger,
      LoggerTransformer,
      "ConsentCallbackQueueHandler",
    )
    private logger: Logger,
    @inject(COOKIES) private cookiesManager: CookiesManager,
  ) {
    const geo = this.cookiesManager.get("Geo");
    this.queue = container.consentCallbackQueue;
    this.optIn = geo
      ? countriesRequiringConsents.includes(
          JSON.parse(decodeURIComponent(geo))?.country?.toLowerCase(),
        )
      : false;

    if (this.queue !== undefined) {
      this.init();
    }
  }

  public resolve(): boolean {
    if (this.wasResolved()) {
      return true;
    }
    if (this.ifAvailableThenResolveIfReady(this.apiConsentResolver)) {
      this.logger.info("API available, resolved?:", this.categories);
      return this.wasResolved();
    }
    if (this.ifAvailableThenResolveIfReady(this.cookieConsentResolver)) {
      this.logger.info("Cookies available, resolved?:", this.categories);
      return this.wasResolved();
    }
    return false;
  }

  private wasResolved() {
    return this.categories !== undefined;
  }

  private init() {
    this.logger.info("Setting up with opt-in status:", this.optIn);
    this.apiConsentResolver = new OnetrustApiConsentResolver(
      this.container,
      this,
    );
    this.cookieConsentResolver = new OnetrustCookieConsentResolver(
      this.optIn,
      this.cookiesManager,
    );
    if (!this.resolve()) {
      setTimeout(() => this.resolveWithFallback(), resolveTimeout);
    }
  }

  private resolveWithFallback(): void {
    if (this.resolve()) {
      return;
    }
    if (
      this.apiConsentResolver.available() ||
      this.cookieConsentResolver.available()
    ) {
      this.logger.info(
        "Still waiting for consent resolve with available api:",
        this.apiConsentResolver.available(),
        "or cookie:",
        this.cookieConsentResolver.available(),
      );
      setTimeout(() => this.resolveWithFallback, resolveTimeout);
    } else {
      this.categories = this.fallback();
      this.logger.info(
        "Unable to resolve consent, fallback to:",
        this.categories,
      );
      this.resolveQueue();
    }
  }

  private ifAvailableThenResolveIfReady(
    resolver: ConsentCategoriesResolver,
  ): boolean {
    const available = resolver.available();

    if (available && resolver.ready()) {
      this.categories = resolver.resolve();
      this.resolveQueue();
    }
    return available;
  }

  private checkAndRun(
    name: string,
    callback: (categories?: Categories) => void,
    requiredCategory?: string,
  ) {
    this.logger.info(
      "About to callback:",
      name,
      "for:",
      requiredCategory,
      "with:",
      this.categories,
    );
    if (requiredCategory === undefined || this.categories[requiredCategory]) {
      callback(this.categories);
      return true;
    }
    return false;
  }

  private resolveQueue() {
    this.flushQueue();
    this.queue.push = (name, callback, category) =>
      this.checkAndRun(name, callback, category);
    this.container.consentCallback = (name, callback, category) =>
      this.checkAndRun(name, callback, category);
  }

  private flushQueue() {
    const queue = this.queue.queue;

    this.logger.info("Consent resolved, flushing queue of size:", queue.length);
    for (let i = queue.length - 1; i >= 0; i--) {
      const callback = queue[i];
      if (
        this.checkAndRun(callback.name, callback.callback, callback.category)
      ) {
        queue.splice(i, 1);
      }
    }
  }

  private fallback(): Categories {
    return this.optIn ? optInCategories : optOutCategories;
  }
}
